import type { Screen0LanguageCode } from "@/features/language/language-registry";
import { friendlyAppName } from "../apps/friendlyAppName";
import { formatWindow } from "../rules/ruleDerivation";
import type { Rule, SmartBlockGoal, Target, TargetApp } from "../rules/ruleTypes";
import type { InsightMetric } from "./insightScoring";
import { ruleDisplaysSmartBlockBadge } from "./smartBlockSelection";

export type ActiveSmartBlockRow = {
  ruleId: string;
  appKey: string;
  displayName: string;
  appIdentifier?: string;
  category?: string;
  /** Omitted for legacy Smart Block rules without a stored goal. */
  goal?: SmartBlockGoal;
  goalLabel?: string;
  windowLabel: string;
  score: number;
  totalSeconds: number;
  opens: number;
};

type MetricLookup = {
  compulsivityByKey: Map<string, InsightMetric>;
  doomByKey: Map<string, InsightMetric>;
};

function metricMaps(
  compulsivityRanked: InsightMetric[],
  doomScrollRiskRanked: InsightMetric[],
): MetricLookup {
  return {
    compulsivityByKey: new Map(compulsivityRanked.map((metric) => [metric.appKey, metric])),
    doomByKey: new Map(doomScrollRiskRanked.map((metric) => [metric.appKey, metric])),
  };
}

function primaryAppForRule(rule: Rule, targetsById: Map<string, Target>): TargetApp | null {
  const target = targetsById.get(rule.targetIds[0]!);
  return target?.apps[0] ?? null;
}

/**
 * Legacy rules without `smartBlockGoal` omit the goal badge in the UI. Sorting uses
 * the higher of compulsivity and doom scores for that app (conservative impact proxy).
 */
function metricForRule(
  appKey: string,
  goal: SmartBlockGoal | undefined,
  lookup: MetricLookup,
): Pick<ActiveSmartBlockRow, "score" | "totalSeconds" | "opens"> {
  const compulsivity = lookup.compulsivityByKey.get(appKey);
  const doom = lookup.doomByKey.get(appKey);

  if (goal === "compulsivity") {
    return {
      score: compulsivity?.score ?? 0,
      totalSeconds: compulsivity?.totalSeconds ?? 0,
      opens: compulsivity?.opens ?? 0,
    };
  }
  if (goal === "doom") {
    return {
      score: doom?.score ?? 0,
      totalSeconds: doom?.totalSeconds ?? 0,
      opens: doom?.opens ?? 0,
    };
  }

  const compScore = compulsivity?.score ?? 0;
  const doomScore = doom?.score ?? 0;
  const metric = compScore >= doomScore ? compulsivity : doom;
  return {
    score: Math.max(compScore, doomScore),
    totalSeconds: metric?.totalSeconds ?? 0,
    opens: metric?.opens ?? 0,
  };
}

function compareActiveRows(a: ActiveSmartBlockRow, b: ActiveSmartBlockRow): number {
  return (
    b.score - a.score ||
    b.totalSeconds - a.totalSeconds ||
    b.opens - a.opens ||
    friendlyAppName(a).localeCompare(friendlyAppName(b)) ||
    a.ruleId.localeCompare(b.ruleId)
  );
}

/** Enabled Smart Block rules with conforming single-app scope, ranked by current metric score. */
export function buildActiveSmartBlockRows(
  rules: Rule[],
  targets: Target[],
  compulsivityRanked: InsightMetric[],
  doomScrollRiskRanked: InsightMetric[],
  copy: { goalCompulsivity: string; goalDoom: string },
  languageCode: Screen0LanguageCode = "en",
): ActiveSmartBlockRow[] {
  const targetsById = new Map(targets.map((target) => [target._id, target]));
  const lookup = metricMaps(compulsivityRanked, doomScrollRiskRanked);
  const rows: ActiveSmartBlockRow[] = [];

  for (const rule of rules) {
    if (!rule.enabled || rule.source !== "smart_block") continue;
    if (!ruleDisplaysSmartBlockBadge(rule, targetsById)) continue;

    const app = primaryAppForRule(rule, targetsById);
    if (!app) continue;

    const goal = rule.smartBlockGoal;
    const metrics = metricForRule(app.key, goal, lookup);

    rows.push({
      ruleId: rule._id,
      appKey: app.key,
      displayName: app.label,
      appIdentifier: app.appIdentifier,
      category: app.category,
      goal,
      goalLabel: goal === "doom" ? copy.goalDoom : goal === "compulsivity" ? copy.goalCompulsivity : undefined,
      windowLabel: formatWindow(rule.window, languageCode),
      ...metrics,
    });
  }

  return rows.sort(compareActiveRows);
}
